import type { recipe } from '../../utils/recipeUtils'
import './RecipeModeMainPage.scss'

import LeftArrow from '../../assets/LeftArrow.svg'
import RightArrow from '../../assets/RightArrow.svg'
import Check from '../../assets/Check.svg'
import Exit from '../../assets/Exit.svg'
import type { User } from '../../utils/loginUtils'
import type { Mode } from '../../App'
import { getMessage } from '../../utils/messageUtils'
import { useState } from 'react'
import Popup from '../PopupForm/Popup'
import NumericInput from '../PopupForm/NumericInput'
import PopupError from '../PopupForm/PopupError'
import { completeRecipe } from '../../utils/storageUtils'

interface RecipeModeMainPageProps {
  recipe: recipe
  user: User
  isMobile: boolean
  setMode: (mode: Mode | null) => void
}

function RecipeModeMainPage({
  recipe,
  user,
  setMode,
  isMobile,
}: RecipeModeMainPageProps) {
  const [step, setStep] = useState(-1)

  // Popup
  const [popupOpen, setPopupOpen] = useState(false)
  const [tuppersToAdd, setTuppersToAdd] = useState(recipe.tuppers)
  const [errorMessage, setErrorMessage] = useState('')

  function goToNextStep() {
    if (!recipe.steps || step < recipe.steps.length - 1) {
      setStep(step + 1)
    }
  }

  function goToPreviousStep() {
    if (step > -1) {
      setStep(step - 1)
    }
  }

  const clickCompleteRecipe = () => {
    setPopupOpen(true)
  }

  function exitRecipeMode() {
    setMode(null)
  }

  const completeExistingRecipe = async () => {
    if (!recipe || !recipe.id) return
    const result = (await completeRecipe(
      user.id!,
      recipe.id,
      tuppersToAdd
    )) as {
      success: boolean
      error?: string
    }
    if (!result.success) {
      setErrorMessage(getMessage('recipe_save_error') + (result.error || ''))
    } else {
      closePopup()
      setMode(null)
    }
  }

  function closePopup() {
    setTuppersToAdd(recipe.tuppers)
    setErrorMessage('')
    setPopupOpen(false)
  }

  function showIngredients() {
    if (step === -1) {
      return (
        <div className="ingredient-list">
          <div className="ingredient-list-title">
            {getMessage('ingredients')}
          </div>
          {recipe.ingredients && recipe.ingredients.length > 0 ? (
            recipe.ingredients.map(ingredient => (
              <div className="ingredient-item" key={ingredient.id}>
                <span>{ingredient.productName}</span>
                <span>
                  {ingredient.quantity}
                  {ingredient.extra ? ` (${ingredient.extra})` : ''}
                </span>
              </div>
            ))
          ) : (
            <div
              className="ingredient-item"
              style={{ justifyContent: 'center', color: '#aaa' }}
            >
              {getMessage('no_ingredients')}
            </div>
          )}
        </div>
      )
    }
    return null
  }

  function showStep() {
    if (step > -1 && recipe.steps && step < recipe.steps.length) {
      return (
        <div className="step">
          <div className="step-title">
            {getMessage('recipe_step_title', [(step + 1).toString()])}
          </div>
          <div className="step-description">{recipe.steps[step].step}</div>
        </div>
      )
    }
    return null
  }

  function getPopupChildren() {
    return (
      <div className={`pop-up-form ${isMobile ? 'pop-up-form-mobile' : ''}`}>
        <PopupError
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />
        <NumericInput
          className={`pop-up-form-label ${isMobile ? 'pop-up-form-label-mobile' : ''}`}
          label={getMessage('tuppers_generated')}
          value={tuppersToAdd}
          setValue={setTuppersToAdd}
          isMobile={isMobile}
        />
      </div>
    )
  }

  return (
    <div
      className={`recipe-mode-main-page ${isMobile ? 'recipe-mode-main-page-mobile' : ''}`}
    >
      {showIngredients()}
      {showStep()}
      <div className="fixed-bottom-buttons">
        <button
          className={`circle-button ${!recipe.steps || step < 0 ? 'hidden' : ''}`}
          aria-label="Previous"
          onClick={goToPreviousStep}
        >
          <img src={LeftArrow} alt="Previous" />
        </button>

        <button
          className="circle-button"
          aria-label="Exit"
          onClick={exitRecipeMode}
        >
          <img src={Exit} alt="Exit" />
        </button>

        {recipe.steps && step === recipe.steps.length - 1 ? (
          <button
            className="circle-button completion-button"
            aria-label="Complete"
            onClick={clickCompleteRecipe}
          >
            <img src={Check} alt="Complete" />
          </button>
        ) : (
          <button
            className={`circle-button ${!recipe.steps || step >= recipe.steps.length - 1 ? 'hidden' : ''}`}
            aria-label="Next"
            onClick={goToNextStep}
          >
            <img src={RightArrow} alt="Next" />
          </button>
        )}
      </div>
      <Popup
        isOpen={popupOpen}
        isMobile={isMobile}
        message={getMessage('recipe_complete_confirm', [
          tuppersToAdd.toString(),
        ])}
        children={getPopupChildren()}
        onOk={completeExistingRecipe}
        onClose={closePopup}
      />
    </div>
  )
}

export default RecipeModeMainPage
